import React from 'react';
import { Form, Stack } from 'react-bootstrap';
import type { ClinicTO, PreviousAdmission } from '../../../../../lib';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import {
  mapToEpisodeDetailsKey,
  mapToPrevAdmissionValue,
} from '../AdditionalSection/utils';
import {
  PatientEpisodeDetailsNames,
  SectionField,
} from '../AdditionalSection/types';

type Value = {
  clinics: ClinicTO[];
};

interface PatientEpisodeDetailsProps {
  field: SectionField;
  value?: Value;
  isEditing?: boolean;
  onInputChange: (event: any) => void;
}

const PatientEpisodeDetails: React.FC<PatientEpisodeDetailsProps> = ({
  field,
  isEditing,
  onInputChange,
}) => {
  const { patientProfile } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  const isPrevAdmission = (
    fieldName: string,
  ): fieldName is PatientEpisodeDetailsNames.PrevAdmission =>
    fieldName === PatientEpisodeDetailsNames.PrevAdmission;

  const normalizeEpisodeDetails = React.useCallback((details: any) => {
    if (Array.isArray(details)) {
      return details.reduce<Record<string, any>>((acc, cur) => {
        const key =
          typeof cur?.meta_name === 'string' ? cur.meta_name.trim() : '';
        if (!key) return acc;
        const v =
          typeof cur?.meta_value === 'string'
            ? cur.meta_value
            : cur?.meta_value;
        acc[key] = v;
        return acc;
      }, {});
    }
    if (details && typeof details === 'object') {
      return { ...details };
    }
    return {};
  }, []);

  const detailsValue: string = React.useMemo(() => {
    const detailsKey = mapToEpisodeDetailsKey(
      (field?.field ?? field?.title) as unknown as string,
    ) as unknown as string;
    const latestClinic = patientProfile?.clinics?.[0];
    const episodes = Array.isArray(latestClinic?.episodes)
      ? latestClinic.episodes
      : [];
    const latestEpisodeIndex = episodes.length > 0 ? episodes.length - 1 : 0;
    const latestEpisode = episodes[latestEpisodeIndex];
    const detailsObj = normalizeEpisodeDetails(latestEpisode?.details);
    const detailsRawValue = detailsObj?.[detailsKey];

    if (detailsRawValue !== undefined) {
      return (
        Array.isArray(detailsRawValue)
          ? detailsRawValue.join(', ')
          : (detailsRawValue ?? '-')
      ) as string;
    }

    const episodeValues = patientProfile.usr_episodes_details;
    const item = episodeValues.find(
      (item) => item.name === field?.title || item.name === field?.field,
    );
    const rawValue = item && 'value' in item ? item.value : undefined;
    if (isPrevAdmission(field?.title)) {
      return mapToPrevAdmissionValue(rawValue as PreviousAdmission);
    }
    return (
      Array.isArray(rawValue) ? rawValue.join(', ') : (rawValue ?? '-')
    ) as string;
  }, [
    field?.field,
    field?.title,
    isPrevAdmission,
    normalizeEpisodeDetails,
    patientProfile?.clinics,
    patientProfile.usr_episodes_details,
  ]);

  return (
    <Stack direction="horizontal" gap={2} className="labelRow w-100">
      <Form.Label className="labelBold">{field.title}</Form.Label>
      {field.editable && isEditing ? (
        <Form.Control
          as="textarea"
          rows={3}
          className="form-control-xs"
          onBlur={(e) =>
            onInputChange({
              target: {
                name: e.target.name,
                value: e.target.value,
              },
              isEpisodeDetail: true,
              detailKey: field.field ?? field.title,
            })
          }
          name={field.title}
          id={field.title}
          placeholder={field.title}
          defaultValue={detailsValue}
        />
      ) : (
        <Form.Label className="labelSimple">{detailsValue}</Form.Label>
      )}
    </Stack>
  );
};

export default PatientEpisodeDetails;
